import { useCallback, useState } from 'react'
import { OktaAuth } from '@okta/okta-auth-js'
import { useGetSpaConfig } from './useGetSpaConfig'
import { AuthStorage } from '../utils/auth'
import { getOrgIssuer } from '../utils/okta/getOrgIssuer'
import { OKTA_MY_ACCOUNT_SCOPES } from '../utils/okta/constants'

type MyAccountAccessTokenState = {
  isLoading: boolean
  error: Error | null
}

export function useMyAccountAccessToken() {
  const { config } = useGetSpaConfig()
  const [state, setState] = useState<MyAccountAccessTokenState>({
    isLoading: false,
    error: null,
  })

  // MyAccount APIs require an org-issuer access token with okta.myAccount.* scopes.
  const getMyAccountAccessToken = useCallback(async (): Promise<string> => {
    const cachedToken = AuthStorage.getOktaMyAccountToken()
    if (cachedToken) return cachedToken

    if (!config?.issuer || !config?.clientId) {
      return ''
    }

    setState({ isLoading: true, error: null })

    try {
      const oktaAuth = new OktaAuth({
        issuer: getOrgIssuer(config.issuer),
        clientId: config.clientId,
        redirectUri: window.location.origin + config.redirectUri,
        tokenManager: {
          storage: 'memory',
          storageKey: 'okta-myaccount-token-storage',
          autoRenew: false,
          autoRemove: true,
        },
      })

      const tokenResponse = await oktaAuth.token.getWithoutPrompt({
        scopes: OKTA_MY_ACCOUNT_SCOPES,
      })

      const accessToken = tokenResponse.tokens.accessToken?.accessToken ?? ''
      const expiresAt = tokenResponse.tokens.accessToken?.expiresAt ?? 0

      if (accessToken && expiresAt) {
        AuthStorage.setOktaMyAccountToken(accessToken, expiresAt)
      }

      return accessToken
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to obtain Okta MyAccount access token')
      setState({ isLoading: false, error })
      return ''
    } finally {
      setState(current => ({ ...current, isLoading: false }))
    }
  }, [config?.clientId, config?.issuer, config?.redirectUri])

  return {
    getMyAccountAccessToken,
    isLoading: state.isLoading,
    error: state.error,
  }
}
